import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, ClipboardCheck, Lock, Play, Sparkles } from 'lucide-react';
import { useAdminStats, useAdminUsers, useConfig, useDeleteStatus, useFavorites, useHistory, usePlaylists, useScanStatus } from '../api/hooks';
import { useAccess } from '../lib/access';
import { API_BASE, api } from '../api/client';
import { AlbumCard, Grid, PersonCard, PlaylistCard, Shelf } from '../components/Cards';
import { Empty, ErrorBox, Loading } from '../components/States';
import { TrackList } from '../components/TrackList';
import { usePlayer, type NormalizeMode, type Quality } from '../player/store';
import { volumeControl } from '../player/engine';
import { toast } from '../ui/toast';
import { fmtBytes } from '../lib/format';
import { setAppearance, useAppearance, type ThemeChoice, type Wallpaper } from '../lib/appearance';

export function PlaylistsPage() {
  const q = usePlaylists();
  return (
    <div className="page">
      <div className="page__bar">
        <h1 className="page__title">Playlists</h1>
        <Link className="btn btn--gold" to="/playlists/smart/new">
          <Sparkles size={16} /> Nouvelle playlist intelligente
        </Link>
      </div>
      {q.isLoading && <Loading />}
      {q.error && <ErrorBox error={q.error} retry={() => q.refetch()} />}
      {q.data && !q.data.length && <Empty title="Aucune playlist">Créez-en une avec le bouton « + » de la barre latérale ou depuis le menu « … » d’un titre.</Empty>}
      <Grid>
        {q.data?.map((p) => (
          <PlaylistCard key={p.id} playlist={p} />
        ))}
      </Grid>
    </div>
  );
}

export function FavoritesPage() {
  const q = useFavorites();
  const playTracks = usePlayer((s) => s.playTracks);
  const d = q.data;
  return (
    <div className="page">
      <div className="page__bar">
        <h1 className="page__title">Favoris</h1>
        {d && d.tracks.length > 0 && (
          <button className="btn btn--gold" onClick={() => playTracks(d.tracks, 0, { source: 'Favoris', shuffle: true })}>
            <Play size={16} fill="currentColor" /> Lire les titres favoris
          </button>
        )}
      </div>
      {q.isLoading && <Loading />}
      {q.error && <ErrorBox error={q.error} retry={() => q.refetch()} />}
      {d && !d.tracks.length && !d.albums.length && !d.artists.length && (
        <Empty title="Pas encore de favoris">Touchez le cœur d’un titre, d’un album ou d’un artiste pour le retrouver ici.</Empty>
      )}
      {d && d.albums.length > 0 && (
        <Shelf title="Albums">
          {d.albums.map((a) => (
            <AlbumCard key={a.id} album={a} />
          ))}
        </Shelf>
      )}
      {d && d.artists.length > 0 && (
        <Shelf title="Artistes">
          {d.artists.map((a) => (
            <PersonCard key={a.id} person={a} kind="artist" />
          ))}
        </Shelf>
      )}
      {d && d.tracks.length > 0 && (
        <section className="section">
          <h2 className="section__title">Titres</h2>
          <TrackList tracks={d.tracks} source="Favoris" showCover showAlbum />
        </section>
      )}
    </div>
  );
}

export function HistoryPage() {
  const q = useHistory();
  return (
    <div className="page">
      <h1 className="page__title">Historique</h1>
      {q.isLoading && <Loading />}
      {q.error && <ErrorBox error={q.error} retry={() => q.refetch()} />}
      {q.data && !q.data.length && <Empty title="Rien écouté pour l’instant">Les titres apparaissent ici dès qu’ils ont été écoutés à moitié.</Empty>}
      {q.data && q.data.length > 0 && <TrackList tracks={q.data} source="Historique" showCover showAlbum />}
    </div>
  );
}

const QUALITIES: { v: Quality; label: string; hint: string }[] = [
  { v: 0, label: 'Originale', hint: 'Fichier source sans conversion (FLAC, Hi-Res…)' },
  { v: 320, label: 'Très haute', hint: 'MP3 320 kbit/s' },
  { v: 192, label: 'Haute', hint: 'MP3 192 kbit/s' },
  { v: 128, label: 'Économe', hint: 'MP3 128 kbit/s, idéal en 4G' },
];

const THEMES: { v: ThemeChoice; label: string }[] = [
  { v: 'auto', label: 'Automatique' },
  { v: 'dark', label: 'Sombre' },
  { v: 'light', label: 'Clair' },
];
const WALLPAPERS: { v: Wallpaper; label: string }[] = [
  { v: 'painting', label: 'Tableau' },
  { v: 'soft', label: 'Discret' },
  { v: 'none', label: 'Uni' },
];

function AppearancePanel() {
  const a = useAppearance();
  return (
    <section className="panel">
      <h2 className="panel__title">Apparence</h2>
      <div className="themes" role="radiogroup" aria-label="Thème">
        {THEMES.map((t) => (
          <button
            key={t.v}
            role="radio"
            aria-checked={a.theme === t.v}
            className={`theme-pick theme-pick--${t.v} ${a.theme === t.v ? 'is-on' : ''}`}
            onClick={() => setAppearance({ theme: t.v })}
          >
            <span className="theme-pick__mini" aria-hidden>
              <i />
              <i />
              <b />
            </span>
            {t.label}
          </button>
        ))}
      </div>
      <div className="appearance__row">
        <span className="appearance__label" id="wall-label">
          Fond d’écran
        </span>
        <div className="seg" role="radiogroup" aria-labelledby="wall-label">
          {WALLPAPERS.map((w) => (
            <button key={w.v} role="radio" aria-checked={a.wallpaper === w.v} className={a.wallpaper === w.v ? 'is-on' : ''} onClick={() => setAppearance({ wallpaper: w.v })}>
              {w.label}
            </button>
          ))}
        </div>
      </div>
      <p className="muted small credit">
        {a.theme === 'auto' && `Automatique suit le réglage de l’appareil (actuellement ${a.resolved === 'light' ? 'clair' : 'sombre'}). `}
        {a.embedded && 'Apparence imposée par l’intranet pour cet onglet ; un choix ici la remplace. '}
        Tableau : Frank Dicksee, <i>La Belle Dame sans Merci</i> (1902), domaine public.
      </p>
    </section>
  );
}

const NORMALIZE: { v: NormalizeMode; label: string; hint: string }[] = [
  { v: 'auto', label: 'Automatique', hint: 'Même niveau pour tous les titres ; un album écouté en entier garde ses nuances' },
  { v: 'track', label: 'Par titre', hint: 'Chaque titre ramené au même niveau (idéal pour les mélanges)' },
  { v: 'album', label: 'Par album', hint: 'Chaque album ramené au même niveau, nuances internes conservées' },
  { v: 'off', label: 'Désactivé', hint: 'Volume des fichiers tel quel' },
];

function PlaybackPanel() {
  const normalize = usePlayer((s) => s.normalize);
  const crossfade = usePlayer((s) => s.crossfade);
  const gapless = usePlayer((s) => s.gapless);
  const radio = usePlayer((s) => s.radio);
  const { setNormalize, setCrossfade, setGapless, toggleRadio } = usePlayer.getState();
  return (
    <section className="panel">
      <h2 className="panel__title">Enchaînements et volume</h2>
      {!volumeControl && (
        <p className="muted small">
          Sur iPhone et iPad, le navigateur ne laisse pas régler le volume : le fondu enchaîné et le volume égalisé n’y sont pas disponibles (la radio infinie, oui).
        </p>
      )}
      <div className="playback">
        <label className="field">
          <span className="field__label">
            Fondu enchaîné : <strong>{crossfade ? `${crossfade} s` : 'désactivé'}</strong>
          </span>
          <input type="range" className="range" min={0} max={12} step={1} value={crossfade} onChange={(e) => setCrossfade(Number(e.target.value))} aria-label="Durée du fondu enchaîné en secondes" />
          <span className="muted small">Entre deux titres d’albums différents. Jamais pour les histoires.</span>
        </label>
        <label className="check">
          <input type="checkbox" checked={gapless} onChange={(e) => setGapless(e.target.checked)} />
          <span>
            <strong>Enchaînement sans blanc</strong>
            <br />
            <span className="muted">Les titres d’un même album s’enchaînent sans coupure ni fondu (concerts, opéras, symphonies, albums mixés).</span>
          </span>
        </label>
        <label className="check">
          <input type="checkbox" checked={radio} onChange={toggleRadio} />
          <span>
            <strong>Radio infinie</strong>
            <br />
            <span className="muted">Quand la file se termine, la lecture continue avec des titres proches (mêmes artistes, genres, époque).</span>
          </span>
        </label>
        <div>
          <span className="field__label">Volume égalisé</span>
          <div className="radios" role="radiogroup" aria-label="Volume égalisé">
            {NORMALIZE.map((n) => (
              <label key={n.v} className={`radio ${normalize === n.v ? 'is-on' : ''}`}>
                <input type="radio" name="normalize" checked={normalize === n.v} onChange={() => setNormalize(n.v)} />
                <span className="radio__label">{n.label}</span>
                <span className="radio__hint">{n.hint}</span>
              </label>
            ))}
          </div>
          <p className="muted small">
            Utilise le ReplayGain des fichiers, ou le volume mesuré par le serveur. Les titres trop forts sont baissés : montez un peu le son de l’appareil si besoin.
          </p>
        </div>
      </div>
    </section>
  );
}

export function SettingsPage() {
  const cfg = useConfig();
  const quality = usePlayer((s) => s.quality);
  const setQuality = usePlayer((s) => s.setQuality);
  const { me, libraries, isAdmin, canUpload } = useAccess();
  const stats = useAdminStats(isAdmin);
  const scan = useScanStatus(isAdmin);
  const users = useAdminUsers(isAdmin);
  const [busy, setBusy] = useState(false);

  const admin = async (path: string, body: unknown, ok: string) => {
    setBusy(true);
    try {
      await api(path, { method: 'POST', json: body });
      toast(ok);
      scan.refetch();
      stats.refetch();
    } catch (e) {
      toast((e as Error).message, 'error');
    } finally {
      setBusy(false);
    }
  };
  const s = scan.data;

  return (
    <div className="page page--narrow">
      <h1 className="page__title">Réglages</h1>

      <AppearancePanel />

      <section className="panel">
        <h2 className="panel__title">Qualité de lecture</h2>
        <div className="radios" role="radiogroup" aria-label="Qualité de lecture">
          {QUALITIES.map((q) => (
            <label key={q.v} className={`radio ${quality === q.v ? 'is-on' : ''}`}>
              <input type="radio" name="quality" checked={quality === q.v} onChange={() => setQuality(q.v)} disabled={q.v > 0 && cfg.data?.features.transcoding === false} />
              <span className="radio__label">{q.label}</span>
              <span className="radio__hint">{q.hint}</span>
            </label>
          ))}
        </div>
      </section>

      <PlaybackPanel />

      {cfg.data?.features.androidApp && (
        <section className="panel">
          <h2 className="panel__title">Application Android</h2>
          <p className="muted small">Ouvrez ce lien depuis votre téléphone, installez l’application, puis connectez-vous avec votre compte Pujolinet.</p>
          <a className="btn btn--gold" href={`${API_BASE}/android.apk`} download>
            Télécharger PUJOLiSIQUE pour Android{cfg.data.features.androidVersion ? ` ${cfg.data.features.androidVersion}` : ''}
          </a>
          <p className="muted small">Une fois installée, l’application propose d’elle-même les nouvelles versions publiées sur ce serveur.</p>
        </section>
      )}

      <section className="panel">
        <h2 className="panel__title">Compte</h2>
        <dl className="info__list">
          <div className="info__row">
            <dt>Nom</dt>
            <dd>{me?.name}</dd>
          </div>
          <div className="info__row">
            <dt>Identifiant Pujolinet</dt>
            <dd>{me?.username}</dd>
          </div>
          {me?.email && (
            <div className="info__row">
              <dt>E-mail</dt>
              <dd>{me.email}</dd>
            </div>
          )}
          <div className="info__row">
            <dt>Bibliothèques</dt>
            <dd>{libraries.map((l) => `${l.name} (${l.trackCount} fichiers)`).join(', ')}</dd>
          </div>
          <div className="info__row">
            <dt>Droits</dt>
            <dd>{[isAdmin && 'administration', canUpload && 'envoi de fichiers', 'écoute'].filter(Boolean).join(', ')}</dd>
          </div>
        </dl>
      </section>

      <section className="panel">
        <h2 className="panel__title">Raccourcis clavier</h2>
        <dl className="keys">
          {[
            ['Espace', 'Lecture / pause'],
            ['N / P', 'Titre suivant / précédent'],
            ['← / →', 'Reculer / avancer de 10 s'],
            ['↑ / ↓', 'Volume'],
            ['M', 'Couper le son'],
            ['L', 'Paroles'],
            ['Q', 'File d’attente'],
            ['/', 'Rechercher'],
          ].map(([k, v]) => (
            <div key={k} className="keys__row">
              <dt>
                <kbd>{k}</kbd>
              </dt>
              <dd>{v}</dd>
            </div>
          ))}
        </dl>
      </section>

      {isAdmin && (
        <>
          <section className="panel">
            <h2 className="panel__title">Analyse des bibliothèques</h2>
            {s && (
              <dl className="info__list">
                <div className="info__row">
                  <dt>État</dt>
                  <dd>
                    {s.running
                      ? `Analyse de ${s.library} en cours : ${s.scanned} fichiers vus, ${s.added} ajoutés`
                      : s.finishedAt
                        ? `Dernière analyse le ${new Date(s.finishedAt).toLocaleString('fr-FR')} (${s.added} ajoutés, ${s.updated} modifiés, ${s.removed} retirés)`
                        : 'Pas encore analysé'}
                    {s.error && <span className="error-text"> Erreur : {s.error}</span>}
                  </dd>
                </div>
                <div className="info__row">
                  <dt>Catalogue</dt>
                  <dd>
                    {s.totals.tracks} fichiers, {s.totals.albums} albums, {s.totals.people} artistes et compositeurs, {s.totals.folders} dossiers
                  </dd>
                </div>
                {stats.data && (
                  <>
                    <div className="info__row">
                      <dt>Conversion audio</dt>
                      <dd>{stats.data.transcoding ? 'ffmpeg disponible' : 'ffmpeg absent : qualité originale uniquement'}</dd>
                    </div>
                    <div className="info__row">
                      <dt>Base d’enrichissement</dt>
                      <dd>
                        {stats.data.lyricsCached} paroles, {stats.data.imagesCached} images, {stats.data.peopleCached} biographies ({stats.data.lyricsMissing + stats.data.imagesMissing} recherches sans résultat)
                      </dd>
                    </div>
                    {stats.data.loudness?.enabled && (
                      <div className="info__row">
                        <dt>Volume égalisé</dt>
                        <dd>
                          {stats.data.loudness.known} titres mesurés
                          {stats.data.loudness.pending > 0 ? `, ${stats.data.loudness.pending} en attente${stats.data.loudness.running ? ' (mesure en cours)' : ''}` : ''}
                          {stats.data.loudness.failed > 0 ? `, ${stats.data.loudness.failed} illisibles` : ''}
                        </dd>
                      </div>
                    )}
                    <div className="info__row">
                      <dt>Activité</dt>
                      <dd>
                        {stats.data.plays} écoutes, {stats.data.sessions} sessions ouvertes
                      </dd>
                    </div>
                  </>
                )}
              </dl>
            )}
            <div className="row-gap">
              <button className="btn btn--gold" disabled={busy || s?.running} onClick={() => void admin('/api/admin/scan', {}, 'Analyse lancée')}>
                Analyser maintenant
              </button>
              <button className="btn" disabled={busy || s?.running} onClick={() => void admin('/api/admin/scan', { force: true }, 'Relecture complète lancée')}>
                Relire tous les fichiers
              </button>
              <button className="btn btn--ghost" disabled={busy} onClick={() => void admin('/api/admin/enrichment/retry', {}, 'Les recherches sans résultat seront relancées')}>
                Relancer les recherches de pochettes et paroles
              </button>
              <Link className="btn btn--ghost" to="/report">
                <ClipboardCheck size={16} /> Rapport sur la bibliothèque
              </Link>
            </div>
          </section>

          <DeletionPanel />

          <section className="panel">
            <h2 className="panel__title">Utilisateurs</h2>
            {users.isLoading && <Loading />}
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">Personne</th>
                    <th scope="col">Bibliothèques</th>
                    <th scope="col">Écoutes</th>
                    <th scope="col">Envois</th>
                    <th scope="col">Dernière visite</th>
                  </tr>
                </thead>
                <tbody>
                  {users.data?.map((u) => (
                    <tr key={u.id}>
                      <td>
                        <span className="strong">{u.name}</span>
                        <span className="muted small"> {u.username}</span>
                      </td>
                      <td>{u.libraries.join(', ') || <span className="error-text">aucune</span>}</td>
                      <td>{u.plays}</td>
                      <td>{u.uploads}</td>
                      <td>{new Date(u.lastSeenAt).toLocaleDateString('fr-FR')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="muted small table-note">Les droits se règlent dans Keycloak : rôles des bibliothèques, rôle d’envoi et rôle d’administration.</p>
          </section>
        </>
      )}
      <p className="muted small">
        {cfg.data?.appName} {cfg.data?.version}
      </p>
    </div>
  );
}

/** Administrateurs : état de la suppression définitive et journal des fichiers effacés */
function DeletionPanel() {
  const st = useDeleteStatus(true);
  const [all, setAll] = useState(false);
  const log = st.data?.log ?? [];
  return (
    <section className="panel">
      <h2 className="panel__title">Suppression de fichiers</h2>
      <p className="muted small">
        Sur une page d’album, de série ou de dossier : bouton « Supprimer ». Sur un titre : clic droit (ou « … ») › « Supprimer le fichier… ». Les fichiers sont effacés du disque du serveur, sans corbeille.
      </p>
      {st.isLoading && <Loading />}
      {st.data && (
        <ul className="del-libs">
          {st.data.libraries.map((l) => (
            <li key={l.id}>
              {l.writable ? <CheckCircle2 size={16} className="ok" aria-hidden /> : <Lock size={16} className="muted" aria-hidden />}
              <span>
                <strong>{l.name}</strong> : {l.writable ? 'suppression possible' : <span className="muted">{l.reason} Les fichiers du dossier « Envois » restent supprimables.</span>}
              </span>
            </li>
          ))}
        </ul>
      )}
      {log.length > 0 && (
        <>
          <h3 className="panel__subtitle">Derniers fichiers supprimés</h3>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">Date</th>
                  <th scope="col">Par</th>
                  <th scope="col">Fichier</th>
                  <th scope="col">Taille</th>
                </tr>
              </thead>
              <tbody>
                {(all ? log : log.slice(0, 10)).map((e, i) => (
                  <tr key={`${e.at}-${i}`}>
                    <td className="nowrap">{new Date(e.at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}</td>
                    <td>{e.by}</td>
                    <td className="break">{e.path}</td>
                    <td className="nowrap">{fmtBytes(e.bytes)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {log.length > 10 && (
            <button className="link-quiet" onClick={() => setAll((v) => !v)}>
              {all ? 'Réduire' : `Voir les ${log.length} dernières suppressions`}
            </button>
          )}
        </>
      )}
    </section>
  );
}
